"use client";

import dynamic from "next/dynamic";
import { useCallback, useMemo, useState } from "react";
import { useRouter } from "next/navigation";
import { BookOpen, Clock3, Share2 } from "lucide-react";
import { buildChronicleShareRoutePath } from "@/lib/chronicles";
import { shareWithSystem } from "@/lib/nativeShare";
import { usePortal } from "@/context/PortalContext";
import { getShareBranding, shareTitle } from "@/lib/portal/shareBranding.mjs";

const ShareModal = dynamic(() => import("@/components/ShareModal"), {
  ssr: false,
  loading: () => null,
});

export default function ChronicleCard({
  title,
  description,
  incidentCountLabel,
  updatedLabel,
  activityLabel = "",
  href,
  hubSlug = "",
  chronicleSlug = "",
  actionMenu = null,
}) {
  const router = useRouter();
  const portal = usePortal();
  const shareBranding = useMemo(() => getShareBranding(portal), [portal]);
  const [shareOpen, setShareOpen] = useState(false);
  const [sharePayload, setSharePayload] = useState(null);

  const buildSharePayload = useCallback(() => {
    if (!hubSlug || !chronicleSlug) return null;
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const sharePath = buildChronicleShareRoutePath(hubSlug, chronicleSlug);
    const url = origin ? `${origin}${sharePath}` : sharePath;
    const cleanDesc = String(description || "").replace(/\s+/g, " ").trim();
    const snippet = cleanDesc
      ? cleanDesc.length > 140
        ? `${cleanDesc.slice(0, 140).trim()}…`
        : cleanDesc
      : `Follow this timeline on ${shareBranding.siteName}.`;

    return {
      url,
      title: shareTitle(title, shareBranding),
      text: `${snippet}\n\n— Chronicle in +${hubSlug} on ${shareBranding.siteName}`,
    };
  }, [chronicleSlug, description, hubSlug, title, shareBranding]);

  const handleShare = useCallback(
    async (event) => {
      event?.preventDefault?.();
      event?.stopPropagation?.();
      const payload = buildSharePayload();
      if (!payload) return;
    if (await shareWithSystem(payload)) return;

      setSharePayload(payload);
      setShareOpen(true);
    },
    [buildSharePayload]
  );

  const openChronicle = useCallback(() => {
    if (href) router.push(href);
  }, [href, router]);

  const handleCardKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      openChronicle();
    },
    [openChronicle]
  );

  return (
    <>
      <article
        role="link"
        tabIndex={0}
        aria-label={`Open ${title}`}
        onClick={openChronicle}
        onKeyDown={handleCardKeyDown}
        className="group relative cursor-pointer rounded-[var(--radius)] border border-[var(--border)] bg-[var(--card)] px-4 py-5 shadow-[var(--shadow-e1)] transition-transform hover:translate-y-[-1px] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] sm:px-6"
      >
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)]">
              <BookOpen className="h-3.5 w-3.5" strokeWidth={1.8} />
              Chronicle
            </div>
            <h3
              className="mt-3 text-[var(--fg-primary)]"
              style={{ fontSize: "16px", lineHeight: "24px", fontWeight: 600 }}
            >
              {title}
            </h3>
            <p
              className="mt-3 max-w-2xl text-[var(--fg-secondary)]"
              style={{ fontSize: "15px", lineHeight: "24px" }}
            >
              {description}
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="text-right">
              <div
                className="text-[var(--fg-primary)]"
                style={{ fontSize: "24px", lineHeight: "24px", fontWeight: 600 }}
              >
                {incidentCountLabel}
              </div>
              <div
                className="mt-1 text-[var(--muted)]"
                style={{ fontSize: "11px", lineHeight: "14px", letterSpacing: "0.18em", textTransform: "uppercase" }}
              >
                Incidents
              </div>
            </div>
            <div onClick={(event) => event.stopPropagation()}>{actionMenu}</div>
          </div>
        </div>

        <div className="mt-5 flex items-end justify-between gap-3 border-t border-[var(--border)] pt-4">
          <div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-4 gap-y-2 text-sm text-[var(--muted)]">
            <span className="inline-flex items-center gap-1.5">
              <Clock3 className="h-4 w-4" strokeWidth={1.8} />
              {updatedLabel}
            </span>
            {activityLabel ? <span className="inline-flex items-center gap-1.5">{activityLabel}</span> : null}
          </div>
          <button
            type="button"
            onClick={handleShare}
            aria-label={`Share ${title}`}
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-[var(--border)] px-4 py-2 text-sm font-medium text-[var(--fg-primary)] hover:border-[var(--brand)] hover:text-[var(--brand)]"
          >
            <Share2 className="h-4 w-4" strokeWidth={1.8} />
            Share
          </button>
        </div>
      </article>

      {shareOpen ? (
        <ShareModal
          open={shareOpen}
          onClose={() => {
            setShareOpen(false);
            setSharePayload(null);
          }}
          payload={sharePayload}
        />
      ) : null}
    </>
  );
}
